import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import CaseImage from "@/components/CaseImage";
import StatusBadge from "@/components/StatusBadge";
import EditStatusEntryDialog from "@/components/dialogs/EditStatusEntryDialog";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/lib/dateUtils";
import api, { photoUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export default function StatusTimelineSection({ history, caseId, onUpdated }) {
  const [editingEntry, setEditingEntry] = useState(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const baselineId = history[0]?.id;
  const sortedHistory = [...history].sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));

  const canDelete = (h) => h.id !== baselineId && (user?.role === "admin" || h.changed_by_id === user?.id);

  const handleDelete = async (entryId) => {
    try {
      await api.delete(`/cases/${caseId}/status/${entryId}`);
      toast({ title: "Timeline entry deleted" });
      onUpdated();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not delete entry", description: err.response?.data?.detail });
    }
  };

  return (
    <div className="mb-8">
      <h2 className="font-display text-xl text-amtm-text mb-3">Status Timeline</h2>
      <div className="space-y-3" data-testid="status-timeline-list">
        {sortedHistory.map((h) => (
          <div key={h.id} className="flex gap-3">
            <div className="w-2 h-2 rounded-full bg-amtm-teal mt-2 shrink-0" />
            <div className="flex-1 bg-amtm-surface border border-amtm-border rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <StatusBadge status={h.status} />
                <div className="flex items-center gap-1">
                  <span className="text-xs text-amtm-textMuted">{formatDate(h.changed_at)}</span>
                  <button
                    onClick={() => setEditingEntry(h)}
                    data-testid={`edit-timeline-entry-button-${h.id}`}
                    className="p-1 rounded-full text-amtm-textMuted hover:text-amtm-teal hover:bg-amtm-tealTint"
                    aria-label="Edit this timeline entry"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {canDelete(h) && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`delete-timeline-entry-button-${h.id}`}
                          aria-label="Delete this timeline entry"
                          className="p-1 rounded-full text-amtm-textMuted hover:text-amtm-error hover:bg-amtm-error/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="delete-timeline-entry-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this update?</AlertDialogTitle>
                          <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-delete-timeline-entry-button">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(h.id)}
                            data-testid="confirm-delete-timeline-entry-button"
                            className="bg-amtm-error hover:bg-amtm-error/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
              {h.note && <p className="text-sm text-amtm-text">{h.note}</p>}
              <p className="text-xs text-amtm-textMuted mt-1">by {h.changed_by_name}</p>
              {h.photo && <CaseImage src={photoUrl(h.photo)} alt="status" className="h-24 w-24 rounded-lg object-cover mt-2" />}
            </div>
          </div>
        ))}
      </div>

      <EditStatusEntryDialog
        open={!!editingEntry}
        onOpenChange={(v) => !v && setEditingEntry(null)}
        caseId={caseId}
        entry={editingEntry}
        onUpdated={onUpdated}
      />
    </div>
  );
}
